import {
  useTopTenParticipantsData,
  useParticipantsPhotos,
  useTeamPhotos,
  useTopTenTeams,
  useGetAllTeamsByInfo,
} from '@frx/frx-react-context';

import { standardizeUrl } from '@frx/frx-services';

import { formatDollarAmount, getPhotoUrl } from '../utils';

import { Leader } from '../types';

import { useTeamsAllowed } from './use-teams-allowed';

/**
 *
 * @returns isLoading boolean
 *          individualLeaders
 */
export const useIndividualLeaders = () => {
  const { data: topTenParticipants, isInitialLoading: isLoadingTopTen } =
    useTopTenParticipantsData();

  const participantsIds = (topTenParticipants?.teamraiserData || []).map(
    ({ id }: { id: number }) => id
  );

  const individualPhotos: any[] = useParticipantsPhotos(participantsIds);
  const individualLeaders: Leader[] =
    topTenParticipants?.teamraiserData?.map((participant: any) => ({
      name: participant.name,
      amountRaised: participant.total,
      url: participant.pageUrl || '',
      avatar: standardizeUrl(
        String(getPhotoUrl(individualPhotos, participant.id)),
        true
      ),
    })) || [];

  const isLoadingIndividualPhotos = individualPhotos.some(
    (photo) => photo.isLoading
  );

  return {
    isLoading: isLoadingIndividualPhotos || isLoadingTopTen,
    individualLeaders,
  };
};

const findTeamPhoto = (teamPhotos: any, id: number): any => {
  const photo = teamPhotos.find(
    (x: any) => x?.data?.captainConsId.toString() === id.toString()
  );
  return photo?.data?.customUrl;
};

/* eslint-disable react-hooks/rules-of-hooks */
// We are disabling this conditional hook warning warning because we have a condition we need to handle.
// If teams are not allowed, we should not attempt to load any team info.

/**
 * @description   Identifies the top 10 teams, and then joins on other API queries to get
 *   the team photos and number of team members.
 * @param eventId
 * @returns
 */
export const useTeamLeaders = () => {
  const teamsAllowed = useTeamsAllowed();

  // Teams are not supported, so don't bother loading any team info.
  if (!teamsAllowed) {
    return { teamsAllowed: false, teamLeaders: [], isLoading: false };
  }

  const { data: topTeams, isInitialLoading: isLoadingTopTeams } =
    useTopTenTeams();

  const teamIds = topTeams?.teamraiserData.map((team) => team.id) || [];
  const teamResults = useGetAllTeamsByInfo(teamIds);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  const teams = teamResults.map((d: any) => d.data);

  const isLoadingTeams = teamResults.some(
    (result: any) => result.isInitialLoading
  );

  const teamPhotos = useTeamPhotos(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    teams.map((team) => team?.captainConsId || 0)
  );
  const isLoadingTeamPhotos = teamPhotos.some(
    (photo) => photo.isInitialLoading
  );

  const teamLeaders = teams.map((team) => ({
    name: team?.name || '',
    amountRaised: formatDollarAmount(team?.amountRaised || 0),
    url: team?.teamPageURL || '',
    avatar: standardizeUrl(
      findTeamPhoto(teamPhotos, team?.captainConsId || 0),
      true
    ),
    numTeamMembers: team?.numMembers,
  }));

  const isLoading = isLoadingTopTeams || isLoadingTeams || isLoadingTeamPhotos;
  return {
    teamsAllowed,
    teamLeaders,
    isLoading,
  };
};
